import { inject, Injector } from '@angular/core';
import { CanMatchFn, Router, Routes } from '@angular/router';

import { AuthService } from '@zeusm/shared/session/auth';
import { PROFILE_NOT_FOUND_PATH, ProfileRouteService, ProfileService, SUPPORT_TARGET_ROUTE } from '@zeusm/shared/session/profile';
import { UserStore, waitForGetMeCompletion } from '@zeusm/shared/session/user';

const signedOutUser: CanMatchFn = () => {
  const auth = inject(AuthService);

  return auth.signedOutCheck();
};

const signedInUser: CanMatchFn = () => {
  const auth = inject(AuthService);

  return auth.signedInCheck();
};

const waitForCurrentUserRoleData = async (): Promise<void> => {
  const injector = inject(Injector);
  const userStore = injector.get(UserStore, null);

  await waitForGetMeCompletion(userStore, injector);
};

async function withTermsAccepted(
  predicate: (termsAccepted: boolean) => boolean,
  redirectTo: string[],
): Promise<true | ReturnType<Router['createUrlTree']>> {
  const auth = inject(AuthService);
  const profile = inject(ProfileService);
  const router = inject(Router);
  const injector = inject(Injector);
  const userStore = injector.get(UserStore, null);

  const authResult = await auth.signedInCheck();

  if (authResult !== true) {
    return authResult;
  }

  await waitForGetMeCompletion(userStore, injector);

  // Missing me means terms are not accepted yet - avoids / ↔ /terms redirect loop.
  const termsAccepted = profile.me()?.termsAccepted ?? false;

  if (predicate(termsAccepted)) {
    return true;
  }

  return router.createUrlTree(redirectTo);
}

const needsTermsAcceptance: CanMatchFn = () => withTermsAccepted(termsAccepted => termsAccepted, ['/terms']);

const canOpenTermsPage: CanMatchFn = () => withTermsAccepted(termsAccepted => !termsAccepted, ['/']);

const canMatchBusinessProfile: CanMatchFn = async () => {
  const profileService = inject(ProfileService);
  const router = inject(Router);

  await waitForCurrentUserRoleData();

  if (profileService.isSupportOnly()) {
    return router.parseUrl(SUPPORT_TARGET_ROUTE);
  }

  return profileService.ensureBusinessProfileActive();
};

const canMatchPersonalProfile: CanMatchFn = async () => {
  const profileService = inject(ProfileService);
  const router = inject(Router);

  await waitForCurrentUserRoleData();

  if (profileService.isSupportOnly()) {
    return router.parseUrl(SUPPORT_TARGET_ROUTE);
  }

  return profileService.ensurePersonalProfileActive();
};

const canMatchSupportProfile: CanMatchFn = async () => {
  const profileService = inject(ProfileService);

  await waitForCurrentUserRoleData();

  return profileService.ensureSupportProfileActive();
};

const isProfilesNotFound: CanMatchFn = () => {
  const profileService = inject(ProfileService);
  const profileRouteService = inject(ProfileRouteService);

  return !profileRouteService.hasAnyProfileAccess(
    profileService.hasPersonalProfileAccess(),
    profileService.hasSupportProfileAccess(),
    profileService.me()?.businessProfiles.length ?? 0,
  );
};

export const appRoutes: Routes = [
  {
    path: 'signin',
    title: 'Sign In',
    loadComponent: () => import('./shared/session/auth/pages/signin/signin.component').then(m => m.SigninComponent),
    canMatch: [signedOutUser],
  },
  {
    path: '404',
    title: '404',
    loadComponent: () => import('./shared/session/pages/not-found/not-found.component').then(m => m.NotFoundComponent),
  },
  {
    path: 'terms',
    title: 'Terms & Conditions',
    loadComponent: () =>
      import('./shared/session/auth/pages/terms-and-conditions/terms-and-conditions.component').then(m => m.TermsAndConditionsComponent),
    canMatch: [signedInUser, canOpenTermsPage],
  },
  {
    path: 'support',
    canMatch: [signedInUser, needsTermsAcceptance, canMatchSupportProfile],
    loadChildren: () => import('./support/support.routes').then(r => r.supportRoutes),
  },
  {
    path: '',
    canMatch: [signedInUser, needsTermsAcceptance],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./shared/session/profile/pages/default-page-by-active-profile/default-page-by-active-profile.component').then(
            m => m.DefaultPageByActiveProfileComponent,
          ),
        pathMatch: 'full',
      },
      {
        path: 'business',
        canMatch: [canMatchBusinessProfile],
        loadChildren: () => import('./business/business.routes').then(r => r.businessRoutes),
      },
      {
        path: 'personal',
        canMatch: [canMatchPersonalProfile],
        loadChildren: () => import('./personal/personal.routes').then(r => r.personalRoutes),
      },
      {
        path: PROFILE_NOT_FOUND_PATH,
        title: 'Profile not found',
        canMatch: [isProfilesNotFound],
        loadComponent: () =>
          import('./shared/session/profile/pages/profile-not-found/profile-not-found.component').then(m => m.ProfileNotFoundComponent),
      },
    ],
  },
  {
    path: '**',
    redirectTo: '404',
  },
];
